import { ApiError } from '@mevaser/shared'
import { Alert, Button, Card, CardContent, Field, Input, OtpInput } from '@mevaser/ui'
import type * as React from 'react'
import { useState } from 'react'
import { api } from '../lib/api.js'

/**
 * The signed-out screens — sign in, forgot password, set a new password — are
 * one flow wearing three URLs, so the chrome and the second-factor step they
 * share live here rather than being copied per page.
 */

/**
 * A full page load rather than a client-side navigation.
 *
 * Authentication has just changed the session cookie, and a reload is the
 * only way to be certain nothing cached from the signed-out state — or from a
 * previous user at a shared counter terminal — survives into the session.
 */
export function completeSignIn() {
  window.location.assign('/')
}

export function SignInShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <div className="flex size-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <span className="text-lg font-bold">M</span>
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Mevaser Tech</h1>
            <p className="text-sm text-foreground-muted">Back office</p>
          </div>
        </div>
        <Card>
          <CardContent className="pt-5">{children}</CardContent>
        </Card>
      </div>
    </div>
  )
}

export type CodeReason = 'send_failed' | 'too_many_codes'
export type CodeChannel = 'email' | 'sms'

const RESEND_COOLDOWN_SECONDS = 30

/**
 * The second factor: the code just emailed to the account, or a recovery code.
 *
 * Reached from two places — a password typed into the sign-in form, and a
 * password just set from a reset link. Both leave the session in the same
 * half-authenticated state, and a reset must not be a side door around 2FA.
 */
export function CodeForm({
  email,
  codeSent,
  codeReason,
  channel: initialChannel = 'email',
  phoneLast4,
}: {
  /** Null when the flow never asked for it — a password just set from a
   *  reset link knows the session, not the address behind it. */
  email: string | null
  codeSent: boolean
  codeReason?: CodeReason
  /** How the first code went out. Absent means email. */
  channel?: CodeChannel
  /** Last four digits of the phone a text went to. */
  phoneLast4?: string
}) {
  const [channel, setChannel] = useState<CodeChannel>(initialChannel)
  const [lastFour, setLastFour] = useState(phoneLast4)
  // If no code went out — delivery is down, or this account already used up
  // its allotment of codes — go straight to the one path that can actually
  // work, rather than a "check your inbox" screen waiting on something that
  // will never arrive.
  const [useRecovery, setUseRecovery] = useState(!codeSent)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle')
  const [cooldown, setCooldown] = useState(0)

  const submit = async (value: string) => {
    setError(null)
    setIsSubmitting(true)
    try {
      await api.post('/auth/2fa/verify', { code: value })
      completeSignIn()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That code is not correct')
      setIsSubmitting(false)
    }
  }

  const onFormSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    void submit(code)
  }

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS)
    const tick = () => {
      setCooldown((n) => {
        if (n <= 1) return 0
        setTimeout(tick, 1000)
        return n - 1
      })
    }
    setTimeout(tick, 1000)
  }

  const onResend = async (requested?: CodeChannel) => {
    setError(null)
    setResendState('sending')
    try {
      const res = await api.post<{ codeChannel: CodeChannel; codePhoneLast4?: string }>(
        '/auth/2fa/resend',
        requested ? { channel: requested } : {},
      )
      setChannel(res.codeChannel)
      setLastFour(res.codePhoneLast4)
      setResendState('sent')
      startCooldown()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send a new code')
      setResendState('idle')
    }
  }

  return (
    <>
      <p className="mb-4 text-sm text-foreground-muted">
        {useRecovery ? (
          'Enter one of your recovery codes.'
        ) : channel === 'sms' ? (
          <>
            We texted a code to your phone
            {lastFour ? (
              <>
                {' '}
                ending in <span className="font-medium text-foreground">{lastFour}</span>
              </>
            ) : null}
            . It expires in 5 minutes.
          </>
        ) : email ? (
          <>
            We emailed a code to <span className="font-medium text-foreground">{email}</span>. It
            expires in 5 minutes.
          </>
        ) : (
          'We emailed a code to this account. It expires in 5 minutes.'
        )}
      </p>
      {!codeSent ? (
        <Alert tone="danger" className="mb-4">
          {codeReason === 'too_many_codes'
            ? "Several sign-in codes were requested for this account recently, so we didn't email another one. Wait a few minutes and try again, or enter a recovery code."
            : "We couldn't email you a code right now — enter a recovery code instead."}
        </Alert>
      ) : null}
      {error ? (
        <Alert tone="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}
      <form onSubmit={onFormSubmit} className="space-y-4" noValidate>
        {useRecovery ? (
          <Field label="Recovery code" required>
            {(props) => (
              <Input
                {...props}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                placeholder="xxxx-xxxx-xxxx"
              />
            )}
          </Field>
        ) : (
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={(value) => void submit(value)}
            disabled={isSubmitting}
            invalid={!!error}
            autoFocus
          />
        )}
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={isSubmitting}>
          Continue
        </Button>
      </form>

      {!useRecovery ? (
        <div className="mt-4 flex items-center justify-between text-xs">
          <button
            type="button"
            className="text-foreground-muted underline-offset-2 hover:underline disabled:opacity-50"
            onClick={() => void onResend()}
            disabled={resendState === 'sending' || cooldown > 0 || codeReason === 'too_many_codes'}
          >
            {codeReason === 'too_many_codes'
              ? 'Too many codes requested — try again later'
              : cooldown > 0
                ? `Resend code (${cooldown}s)`
                : resendState === 'sending'
                  ? 'Sending…'
                  : resendState === 'sent'
                    ? 'Code sent — resend again'
                    : 'Resend code'}
          </button>
          {channel === 'sms' ? (
            <button
              type="button"
              className="text-foreground-muted underline-offset-2 hover:underline disabled:opacity-50"
              onClick={() => void onResend('email')}
              disabled={resendState === 'sending' || cooldown > 0}
            >
              Email me the code instead
            </button>
          ) : null}
        </div>
      ) : null}

      <p className="mt-3 text-center text-xs">
        <button
          type="button"
          className="text-foreground-muted underline-offset-2 hover:underline"
          onClick={() => {
            setUseRecovery((v) => !v)
            setCode('')
            setError(null)
          }}
        >
          {useRecovery ? 'Use the emailed code instead' : 'Use a recovery code instead'}
        </button>
      </p>
    </>
  )
}
